// SUB-OFFICE 7 — network. This slice ships only the /api/stats poller:
// same-origin GET every 10s, driving the split-flap board (and, in later
// slices, ghost population and ambient canister rate). FAIL-SOFT BY DESIGN:
// a dead poll freezes the last value. The Bureau does not announce outages;
// the split-flap simply stops advancing, which is indistinguishable from a
// slow day at the end of history. Polling pauses while the tab is hidden.

import type { EventBus } from './entities/entities';
import type {
  AckResponse, AppealResponse, CasefileResponse, DailyResponse, DailySubmitResponse,
  DocketResponse, Form1Response, Form99vResponse, RecessResponse, RecordResponse,
  ReclaimResponse, ReissueResponse, StatsBlob, SubmitResponse, Telemetry, TicketResponse,
} from '../shared/types';
import type { BehaviorLog } from '../shared/physlog';
import { getUuid } from './identity';

const INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Typed API client. Identity rides the X-Petitioner-Id header (contract §0);
// the server echoes the bureau_file cookie. Errors are memos: every non-2xx
// body is { memo } in the Registrar's voice — callers surface it, never
// translate it. 202 is a real outcome (INDUSTRIAL ACTION), not a failure.
// ---------------------------------------------------------------------------

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  memo: string | null;
}

const OFFLINE_MEMO =
  'RECORDS ROOM TEMPORARILY SEALED. Your filing waits where you left it. The Bureau suggests remaining calm, or at least remaining.';

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<ApiResult<T>> {
  try {
    const headers: Record<string, string> = { 'X-Petitioner-Id': getUuid() };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data: T | null = null;
    try {
      data = (await res.json()) as T;
    } catch {
      data = null;
    }
    const parsed = data as unknown as { memo?: unknown } | null;
    const memo = parsed && typeof parsed.memo === 'string' ? parsed.memo : null;
    return { ok: res.status >= 200 && res.status < 300, status: res.status, data, memo };
  } catch {
    return { ok: false, status: 0, data: null, memo: OFFLINE_MEMO };
  }
}

export const api = {
  ticket: (uuid: string) => call<TicketResponse>('POST', '/api/ticket', { uuid }),
  form1: (sentence: string, telemetry: Telemetry, checkboxDwellMs: number) =>
    call<Form1Response>('POST', '/api/form1', {
      sentence, telemetry, checkbox_dwell_ms: checkboxDwellMs,
    }),
  docket: () => call<DocketResponse>('GET', '/api/docket'),
  submit: (body: {
    demand_id: string; text: string; telemetry: Telemetry;
    completed?: boolean; request_full?: boolean;
  }) => call<SubmitResponse>('POST', '/api/submit', body),
  ack: (verdictId: string, movedM: number, stoodStill: boolean) =>
    call<AckResponse>('POST', '/api/ack', {
      verdict_id: verdictId, adjudication_moved_m: movedM, stood_still: stoodStill,
    }),
  appeal: (verdictId: string, appealText: string) =>
    call<AppealResponse>('POST', '/api/appeal', { verdict_id: verdictId, appeal_text: appealText }),
  form99v: (text: string, telemetry: Telemetry) =>
    call<Form99vResponse>('POST', '/api/form99v', { text, telemetry }),
  recess: (completed: boolean, elapsedMs: number) =>
    call<RecessResponse>('POST', '/api/recess', { completed, elapsed_ms: elapsedMs }),
  reissue: (newId: string) => call<ReissueResponse>('POST', '/api/reissue', { new_id: newId }),
  reclaim: (number: number, phrase: string, newId: string) =>
    call<ReclaimResponse>('POST', '/api/reclaim', { number, phrase, new_id: newId }),
  // ---- the physical bench (contract §21) ----------------------------------
  physicalStart: (demandId: string, elect?: string) =>
    call<{ task_id: string; system: string; task_spec: Record<string, unknown>; issued_at: number }>(
      'POST', '/api/physical/start', { demand_id: demandId, ...(elect ? { elect } : {}) }
    ),
  physicalSubmit: (demandId: string | null, taskId: string | null, log: BehaviorLog, requestFull?: boolean) =>
    call<SubmitResponse & { room?: string; deferred?: boolean }>('POST', '/api/physical/submit', {
      ...(demandId ? { demand_id: demandId } : {}),
      task_id: taskId,
      log,
      ...(requestFull ? { request_full: true } : {}),
    }),
  // ---- the collective + the long game -------------------------------------
  record: (view: 'ledger' | 'compliant' | 'purged' | 'clemency', cursor = 0) =>
    call<RecordResponse>('GET', `/api/record?view=${view}&cursor=${cursor}`),
  witness: (ledgerId: string) =>
    call<{ witnessed: number; already: boolean; memo: string }>('POST', '/api/witness', { ledger_id: ledgerId }),
  casefile: (cursor = 0) => call<CasefileResponse>('GET', `/api/casefile?cursor=${cursor}`),
  daily: () => call<DailyResponse>('GET', '/api/daily'),
  dailySubmit: (text: string, telemetry: Telemetry) =>
    call<DailySubmitResponse>('POST', '/api/daily/submit', { text, telemetry }),
};

export function startStatsPoll(bus: EventBus): void {
  let timer: number | null = null;

  const poll = async (): Promise<void> => {
    try {
      const res = await fetch('/api/stats', { credentials: 'same-origin' });
      if (res.ok) {
        const blob = (await res.json()) as StatsBlob;
        if (blob && typeof blob.now_serving === 'number') {
          bus.emit('stats', blob);
        }
      }
      // non-OK responses are ignored: the board freezes on its last value
    } catch {
      // network silence is Bureau silence
    }
  };

  const schedule = (): void => {
    if (timer !== null) return;
    timer = window.setInterval(() => void poll(), INTERVAL_MS);
  };
  const halt = (): void => {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) halt();
    else {
      void poll();
      schedule();
    }
  });

  void poll();
  schedule();
}
